import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { switchMap } from 'rxjs';
import { getHtmlLangAttribute } from '../i18n/app-locale';

/**
 * Keeps `document.documentElement.lang` in sync with Transloco's active
 * locale (PFL-1900). `<html lang>` was hardcoded `en` in `index.html` and
 * never updated at runtime, so screen readers/browser language tools kept
 * applying English pronunciation rules to Vietnamese content on every route
 * — `vn` (Vietnamese, Transloco's own locale id) maps to the standards-valid
 * BCP-47 tag `vi` via `getHtmlLangAttribute`; `en` passes through unchanged.
 *
 * `TranslocoService.langChanges$` is a `BehaviorSubject`, so subscribing
 * here — regardless of provider registration order relative to the
 * language-bootstrap `provideAppInitializer` in `app.config.ts` — always
 * replays the current active lang immediately, and every subsequent
 * `setActiveLang()` call (e.g. from the header language switcher) re-fires
 * synchronously with no page reload required.
 *
 * Deliberately app-wide rather than tied to the header's `LanguagesComponent`
 * (which only exists on authenticated shell routes): sign-in and other
 * unauthenticated routes still render `<html lang>` correctly since this
 * service is a `providedIn: 'root'` singleton, eagerly instantiated via
 * `provideEnvironmentInitializer` in `app.config.ts` — same wiring pattern
 * as `TenantThemeService`.
 */
@Injectable({ providedIn: 'root' })
export class DocumentLangService {
    private readonly _document = inject(DOCUMENT);
    private readonly _translocoService = inject(TranslocoService);

    constructor() {
        this._translocoService.langChanges$.subscribe((activeLang) => {
            this._document.documentElement.lang =
                getHtmlLangAttribute(activeLang);
        });
        // The static index.html title is the VI default; follow the active language.
        this._translocoService.langChanges$
            .pipe(
                switchMap(() =>
                    this._translocoService.selectTranslate('common.appTitle')
                )
            )
            .subscribe((title) => (this._document.title = title));
    }
}
